
import React from 'react';
import { Button } from '@/components/ui/button';
import { Edit } from 'lucide-react';
import MedicalHistoryEdit from '../MedicalHistoryEdit';

interface MedicalHistorySectionProps {
  data: {
    hasChronicConditions: boolean;
    chronicConditionsDetails: string;
    takingMedications: boolean;
    medicationsDetails: string;
    lastPeriodStart: Date | null;
    lastPeriodDuration: string;
    hadGynSurgery: boolean;
    gynSurgeryDetails: string;
    experiencedDepression: boolean;
    highStressLevels: boolean;
    healthyDiet: boolean;
    exerciseFrequency: string;
  };
  editMode: string | null;
  onEditToggle: (section: string) => void;
  onSave: (section: string, data: any) => void;
}

const MedicalHistorySection: React.FC<MedicalHistorySectionProps> = ({ 
  data, 
  editMode, 
  onEditToggle, 
  onSave 
}) => {
  return (
    <>
      {editMode === 'medicalHistory' ? (
        <MedicalHistoryEdit 
          data={data} 
          onSave={(data) => onSave('medicalHistory', data)}
          onCancel={() => onEditToggle('medicalHistory')}
        />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Chronic Conditions</span>
              <span className="font-medium">{data.hasChronicConditions ? 'Yes' : 'No'}</span>
            </div>
            {data.hasChronicConditions && (
              <div className="flex justify-between p-3 bg-muted/50 rounded-md col-span-2">
                <span className="text-muted-foreground">Details</span>
                <span className="font-medium">{data.chronicConditionsDetails}</span>
              </div>
            )}
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Taking Medications</span>
              <span className="font-medium">{data.takingMedications ? 'Yes' : 'No'}</span>
            </div>
            {data.takingMedications && (
              <div className="flex justify-between p-3 bg-muted/50 rounded-md col-span-2">
                <span className="text-muted-foreground">Medications</span>
                <span className="font-medium">{data.medicationsDetails}</span>
              </div>
            )}
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Last Period</span>
              <span className="font-medium">
                {data.lastPeriodStart?.toLocaleDateString()}
              </span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Period Duration</span>
              <span className="font-medium">{data.lastPeriodDuration} days</span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Had Gynecological Surgery</span>
              <span className="font-medium">{data.hadGynSurgery ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Experienced Depression/Anxiety</span>
              <span className="font-medium">{data.experiencedDepression ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">High Stress Levels</span>
              <span className="font-medium">{data.highStressLevels ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Maintains Healthy Diet</span>
              <span className="font-medium">{data.healthyDiet ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between p-3 bg-muted/50 rounded-md">
              <span className="text-muted-foreground">Exercise Frequency</span>
              <span className="font-medium">{data.exerciseFrequency}</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => onEditToggle('medicalHistory')}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Medical History
          </Button>
        </div>
      )}
    </>
  );
};

export default MedicalHistorySection;
